import { createSelector } from '@reduxjs/toolkit';
import { currentUserUuid } from '../user/currentUserUuid';
import { currentWeekEventOccurrences } from './current-week-event-occurrences';
import { employeesAndContactsOptions } from '../organization/employeesAndContactsOptions';
import { getContactsAsUsers } from '../partners/get-contacts-as-users';
import { holidays } from './holidays';
import { weekStart } from './week-start';
import { getOrganization } from '../organization/getOrganization';
import { singleUserVersion } from '../organization/singleUserVersion';
import { meetings } from '../meetings/meetings';
import { employeesShortNamesAndI } from '../employees/employees-short-names-and-i';
import { today } from './today';
import { currentWeekTodoEventOccurrences } from './current-week-todo-event-occurrences';
import { functionsNotDeleted } from '../responsibilities/functions-not-deleted';
import type { CalendarDay } from './calendar-day';
import { LocalDate } from '../../../utilities/local-date';
import { homePageEventItem } from './home-page-event-item';
import { eventDoneText } from '../events/event-done-text';
import { timeDescription } from '../../utilities';
import { sortBy } from 'lodash';
import { meetingParticipantsText } from '../../meetingParticipantsText';
import { t as translate } from 'src/i18n';

export const calendarTaskInstances = createSelector(
  currentUserUuid,
  currentWeekEventOccurrences,
  employeesAndContactsOptions,
  getContactsAsUsers,
  holidays,
  weekStart,
  getOrganization,
  singleUserVersion,
  meetings,
  employeesShortNamesAndI,
  today,
  currentWeekTodoEventOccurrences,
  functionsNotDeleted,
  (
    currentUserUuid,
    eventOccurrences,
    employeesAndContacts,
    contactsAsUsers,
    holidays,
    weekStart,
    organization,
    singleUserVersion,
    meetings,
    employeesShortNamesAndI,
    today,
    eventOccurrencesForTodo,
    functions,
  ): CalendarDay[] => {
    if (organization === undefined) return [];

    const days: CalendarDay[] = [];
    const t = LocalDate.fromString(today);

    for (let d = 0; d < 7; d++) {
      const day: CalendarDay = {
        date: LocalDate.fromString(weekStart).plusDays(d).toString(),
        holiday: '',
        tasks: [],
      };

      const holiday = holidays.filter((holiday) =>
        LocalDate.fromString(holiday.date).isSame(LocalDate.fromString(day.date)),
      )[0];

      if (holiday) {
        day.holiday = holiday.description;
      }
      eventOccurrences
        .filter((item) => LocalDate.fromString(item.date).isSame(LocalDate.fromString(day.date)))
        .forEach((e) => {
          const overdue = e.date !== null && e.persistent && !e.doneDate && LocalDate.fromString(e.date).isBefore(t);
          const item = homePageEventItem(
            e,
            overdue,
            organization,
            employeesAndContacts,
            currentUserUuid ?? '',
            contactsAsUsers,
            employeesShortNamesAndI,
            singleUserVersion,
            functions,
          );
          let doneText = '';
          if (e.doneDate) {
            doneText = translate('content.donePrefix') + eventDoneText(e, employeesAndContacts, singleUserVersion);
          }
          day.tasks.push({
            timeOfDayDisplay: timeDescription(e.time ?? '', e.durationMinutes),
            instanceTime: e.time,
            uuid: e.uuid,
            name: e.name ?? '',
            eventNotes: item.eventNotes,
            assignee: item.assignee && !singleUserVersion ? item.assignee : '',
            href: item.href,
            myTask: item.myTask,
            dateTimeForOrdering: item.dateTimeForOrdering,
            persistent: e.persistent,
            overdue: item.overdue,
            doneDate: item.doneDate,
            doneByEmployeeUuid: item.doneByEmployeeUuid,
            doneText: doneText,
            locked: false,
            accessible: true,
            hasDraft: item.hasDraft,
            isConfirmedEntity: e.isConfirmedEntity,
            draftName: e.draftName ?? '',
            progress: item.progress,
          });
        });
      eventOccurrencesForTodo
        .filter((item) => LocalDate.fromString(item.doneDate).isSame(LocalDate.fromString(day.date)))
        .forEach((e) => {
          const overdue = false;
          const item = homePageEventItem(
            e,
            overdue,
            organization,
            employeesAndContacts,
            currentUserUuid ?? '',
            contactsAsUsers,
            employeesShortNamesAndI,
            singleUserVersion,
            functions,
          );
          let doneText = '';
          if (e.doneDate) {
            doneText = translate('content.donePrefix') + eventDoneText(e, employeesAndContacts, singleUserVersion);
          }
          day.tasks.push({
            timeOfDayDisplay: '',
            instanceTime: '',
            uuid: e.uuid,
            name: e.name ?? '',
            eventNotes: item.eventNotes,
            assignee: item.assignee && !singleUserVersion ? item.assignee : '',
            href: item.href,
            myTask: item.myTask,
            dateTimeForOrdering: item.dateTimeForOrdering,
            persistent: e.persistent,
            overdue: item.overdue,
            doneDate: item.doneDate,
            doneByEmployeeUuid: item.doneByEmployeeUuid,
            doneText: doneText,
            locked: false,
            accessible: true,
            hasDraft: item.hasDraft,
            isConfirmedEntity: e.isConfirmedEntity,
            draftName: e.draftName ?? '',
            progress: item.progress,
          });
        });
      if (!singleUserVersion) {
        const occurrences = meetings.flatMap((m) => {
          return m.meetingOccurrences.filter((o) => o.date === day.date);
        });
        occurrences.forEach((o) => {
          const item = organization.meetingOccurrences.find((x) => x.uuid === o.uuid);
          const assignee = meetingParticipantsText(item, functions, employeesShortNamesAndI, contactsAsUsers, true);
          let myTask = false;
          if (item) {
            if (
              item.participants.includes(currentUserUuid) ||
              item.responsibleEmployeeUuid?.includes(currentUserUuid) ||
              item.externalParticipants.includes(currentUserUuid)
            ) {
              myTask = true;
            }
            if (item.responsibleUuid) {
              const responsibleFunction = organization.functionsById[item.responsibleUuid];
              if (responsibleFunction?.employees.includes(currentUserUuid)) {
                myTask = true;
              }
            }
          }
          if (item !== undefined) {
            let href = '/account/' + organization.organizationId + '/318434/meetingOccurrences/' + o.uuid;
            if (!o.isConfirmedEntity) {
              href += '?edit';
            }
            day.tasks.push({
              timeOfDayDisplay: timeDescription(item.time ?? '', item.durationMinutes),
              instanceTime: item.time,
              uuid: item.uuid,
              name: item.name ?? '',
              eventNotes: '',
              assignee,
              href,
              myTask,
              dateTimeForOrdering: item.date + ' ' + (item.time && item.time !== 'NONE' ? item.time : '00:00'),
              persistent: false,
              overdue: false,
              doneDate: '',
              doneByEmployeeUuid: '',
              doneText: '',
              locked: o.classified,
              accessible: !o.restricted,
              hasDraft: item.hasDraft,
              meeting: true,
              isConfirmedEntity: o.isConfirmedEntity,
              draftName: o.draftName ?? '',
            });
          }
        });
      }
      day.tasks = sortBy(day.tasks, ['dateTimeForOrdering']);
      days.push(day);
    }
    return days;
  },
);
